import React, { useEffect, useRef, useState } from 'react';
import type { WalletDTO } from '@push.io/shared';
import { NetworkClient } from '../../network/client.js';
import { GameEngine } from '../../game/engine.js';
import { AudioManager } from '../../audio/manager.js';
import { Header } from './Header.js';
import { PlayPanel } from './PlayPanel.js';
import { SettingsPanel } from './SettingsPanel.js';
import { Footer } from './Footer.js';
import { LegalModal, type LegalDocument } from './LegalModal.js';
import { TutorialOfferDialog } from '../tutorial/TutorialOfferDialog.js';
import { saveTutorialChoice, shouldOfferTutorial } from '../../tutorial/tutorial-storage.js';
import { DEFAULT_MENU_SETTINGS, getDefaultControlScheme, type MenuSettings, type ServerStats } from './types.js';
import { AccountBar } from './AccountBar.js';
import { useAccountController, useAccountSnapshot } from '../../account/context.js';
import './main-menu.css';

const SETTINGS_KEY = 'pushio_menu_settings';
const NAME_KEY = 'pushio_player_name';

function loadSettings(): MenuSettings {
  try {
    const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
    if (saved.language === 'en') saved.language = 'en-GB';
    if (!saved.controlScheme || saved.controlScheme === 'auto') saved.controlScheme = getDefaultControlScheme();
    return { ...DEFAULT_MENU_SETTINGS, ...saved };
  } catch {
    return { ...DEFAULT_MENU_SETTINGS, controlScheme: getDefaultControlScheme() };
  }
}

export function MainMenu({ network, gameEngine, audio, wallet, onStartTutorial, onCustomize, onStartBots }: {
  network: NetworkClient;
  gameEngine: GameEngine;
  audio: AudioManager;
  wallet: WalletDTO | null;
  onStartTutorial: () => void;
  onCustomize: () => void;
  onStartBots: () => void;
}) {
  const account = useAccountController();
  const accountSnapshot = useAccountSnapshot();
  const [settings, setSettings] = useState<MenuSettings>(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [legalDocument, setLegalDocument] = useState<LegalDocument | null>(null);
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || '');
  const [stats, setStats] = useState<ServerStats | null>(null);
  const [queueStatus, setQueueStatus] = useState({
    inQueue: false,
    position: 0,
    playersWaiting: 0,
    estimatedWait: 0,
  });
  const [tutorialOfferOpen, setTutorialOfferOpen] = useState(false);
  const particlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const registeredAccount = accountSnapshot.account?.accountType === 'REGISTERED'
      ? accountSnapshot.account
      : null;
    if (!registeredAccount) return;

    const serverName = registeredAccount.name.trim();
    const localName = localStorage.getItem(NAME_KEY)?.trim() || '';
    if (serverName && serverName !== 'Player') {
      setName(serverName);
      localStorage.setItem(NAME_KEY, serverName);
    } else if (localName) {
      void account.updatePlayerName(localName);
    }
  }, [account, accountSnapshot.account]);

  useEffect(() => {
    let frame = 0;
    // Pointer tracking — shared across particle updates, throttled at 60fps
    let pointerX = -9999;
    let pointerY = -9999;
    const REPULSE_RADIUS = 110; // px
    const REPULSE_STRENGTH = 28; // max px nudge at contact point

    const onPointerMove = (e: PointerEvent) => {
      pointerX = e.clientX;
      pointerY = e.clientY;
    };
    const onPointerLeave = () => { pointerX = -9999; pointerY = -9999; };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave, { passive: true });

    const animate = (now: number) => {
      const container = particlesRef.current;
      const particles = container?.children;
      if (particles && container) {
        const rect = container.getBoundingClientRect();
        for (let index = 0; index < particles.length; index++) {
          const phase = now / 16000 * Math.PI * 2 + index * 1.37;
          const particle = particles[index] as HTMLElement;

          // Base idle wave
          const baseX = Math.sin(phase) * 14;
          const baseY = Math.cos(phase * 0.83) * 24;

          // Compute this particle's approximate screen position using its CSS left/top
          const pctX = parseFloat(particle.style.left || (getComputedStyle(particle).left)) || 0;
          const pctY = parseFloat(particle.style.top || (getComputedStyle(particle).top)) || 0;
          const px = rect.left + (pctX / 100) * rect.width;
          const py = rect.top + (pctY / 100) * rect.height;

          // Repulsion vector
          const dx = px - pointerX;
          const dy = py - pointerY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          let nudgeX = 0;
          let nudgeY = 0;
          if (dist < REPULSE_RADIUS && dist > 0) {
            const force = (1 - dist / REPULSE_RADIUS) * REPULSE_STRENGTH;
            nudgeX = (dx / dist) * force;
            nudgeY = (dy / dist) * force;
          }

          particle.style.transform =
            `translate(${baseX + nudgeX}px, ${baseY + nudgeY}px) scale(${1.15 + Math.sin(phase * 1.11) * 0.35})`;
          particle.style.opacity = String(0.16 + (Math.sin(phase) + 1) * 0.08);
        }
      }
      frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  useEffect(() => {
    network.onQueueStatusEvent((data: any) => setQueueStatus({
      inQueue: !!data.inQueue,
      position: data.position || 0,
      playersWaiting: data.playersWaiting || 0,
      estimatedWait: data.estimatedWait || 0,
    }));
    network.requestQueueStatus();
    const queueTimer = setInterval(() => network.requestQueueStatus(), 3000);
    return () => {
      clearInterval(queueTimer);
      network.onQueueStatusEvent(null);
    };
  }, [network]);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      const startedAt = performance.now();
      try {
        const response = await fetch(`/stats?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('stats unavailable');
        const data = await response.json();
        if (active) setStats({
          sessions: data.sessions,
          rooms: data.rooms,
          queue: data.queue,
          ping: Math.max(1, Math.round(performance.now() - startedAt)),
        });
      } catch {
        if (active) setStats(null);
      }
    };
    refresh();
    const statsTimer = setInterval(refresh, 2000);
    return () => { active = false; clearInterval(statsTimer); };
  }, []);

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    gameEngine.setControlScheme(settings.controlScheme);
    gameEngine.setMouseSensitivity(settings.mouseSensitivity);
    gameEngine.setGraphicsQuality(settings.graphicsQuality);
    audio.setVolume(settings.soundVolume);
    audio.setMusicVolume(settings.musicVolume);
  }, [settings, gameEngine, audio]);

  const updateSettings = (patch: Partial<MenuSettings>) => {
    const definedPatch = Object.fromEntries(
      Object.entries(patch).filter(([, value]) => value !== undefined),
    ) as Partial<MenuSettings>;
    setSettings((current) => ({ ...current, ...definedPatch }));
  };

  const preparePlayer = () => {
    const finalName = name.trim() || `Jugador${Math.floor(1000 + Math.random() * 9000)}`;
    const cleanName = finalName.replace(/\s+/g, ' ').slice(0, 16);
    setName(cleanName);
    localStorage.setItem(NAME_KEY, cleanName);
    network.setPlayerName(cleanName);
    void account.updatePlayerName(cleanName);
  };

  const play = () => {
    preparePlayer();
    if (shouldOfferTutorial()) {
      setTutorialOfferOpen(true);
      return;
    }
    network.joinQueue();
    setQueueStatus((current) => ({ ...current, inQueue: true }));
  };

  return (
    <div className="main-menu-shell">
      <div ref={particlesRef} className="menu-particles" aria-hidden="true">
        {Array.from({ length: 9 }, (_, index) => <i key={index} />)}
      </div>
      <Header language={settings.language}
        wallet={wallet}
        onSettings={() => setSettingsOpen(true)}
        onLanguage={(language) => updateSettings({ language })}
        accountButton={<AccountBar language={settings.language} />} />
      <PlayPanel
        language={settings.language}
        name={name}
        inQueue={queueStatus.inQueue}
        position={queueStatus.position}
        playersWaiting={queueStatus.playersWaiting}
        estimatedWait={queueStatus.estimatedWait}
        stats={stats}
        onNameChange={(value) => setName(value.slice(0, 16))}
        onPlay={play}
        onLeave={() => {
          network.leaveQueue();
          setQueueStatus({ inQueue: false, position: 0, playersWaiting: 0, estimatedWait: 0 });
        }}
        onCustomize={onCustomize}
        onPlayBots={onStartBots}
        audio={audio}
      />
      <Footer language={settings.language} stats={stats} showPing={settings.showPing} showFps={settings.showFps}
        onPrivacy={() => setLegalDocument('privacy')} onTerms={() => setLegalDocument('terms')} />
      {settingsOpen && <SettingsPanel language={settings.language} settings={settings} onChange={updateSettings}
        onClose={() => setSettingsOpen(false)}
        onRepeatTutorial={() => {
          preparePlayer();
          setSettingsOpen(false);
          onStartTutorial();
        }}
        onPrivacy={() => setLegalDocument('privacy')} onTerms={() => setLegalDocument('terms')} />}
      {legalDocument && <LegalModal document={legalDocument} language={settings.language}
        onClose={() => setLegalDocument(null)} />}
      {tutorialOfferOpen && <TutorialOfferDialog language={settings.language}
        onTutorial={() => {
          setTutorialOfferOpen(false);
          onStartTutorial();
        }}
        onSkip={() => {
          saveTutorialChoice('skipped');
          setTutorialOfferOpen(false);
          network.joinQueue();
          setQueueStatus((current) => ({ ...current, inQueue: true }));
        }} />}
    </div>
  );
}
